import { useEffect, useRef, useState } from "react";
import moment from "moment";
import _ from "lodash";
import { toast } from "react-toastify";

import { getAvatarUrl, getImageUrl } from "src/Common/common";
import TechnicianService from "src/Services/API/technician.service";
import { can, handleError, isAccountant, isManagerOver } from "src/Common/function";

const EMPTY_TIME = "00:00";

const DayTechItem = ({
  tech,
  currentDate,
  startDateString,
  timesheetUpdated,
  activeEditingTechId,
  setActiveEditingTechId,
  woStatus,
}) => {
  const [techData, setTechData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Raw strings the user is currently typing. Only normalized on commit.
  const [editedTimes, setEditedTimes] = useState({
    regularTime: EMPTY_TIME,
    travelTime: EMPTY_TIME,
    overTime: EMPTY_TIME,
  });

  // Snapshot of normalized times at the moment editing started. Used as the
  // diff baseline on save so we don't send unchanged fields to the API.
  const baselineTimesRef = useRef({
    regularTime: EMPTY_TIME,
    travelTime: EMPTY_TIME,
    overTime: EMPTY_TIME,
  });

  const isEditing = activeEditingTechId === tech?.id;
  const isFutureStartDate = moment(startDateString).isAfter(moment(), "day");

  /**
   * Normalize an HH:MM string. Anything malformed falls back to "00:00".
   * Only used for values we already believe are in HH:MM shape.
   */
  const normalizeTimeValue = (value) => {
    if (!value || typeof value !== "string") return EMPTY_TIME;
    const trimmed = value.trim();
    if (!trimmed || trimmed === "__:__") return EMPTY_TIME;

    const parts = trimmed.split(":");
    if (parts.length !== 2) return EMPTY_TIME;

    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return EMPTY_TIME;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return EMPTY_TIME;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  /**
   * Parse whatever the user typed into a canonical HH:MM string.
   * Supports:
   *   ""           -> "00:00"
   *   "1"          -> "01:00"     (whole hours)
   *   "10"         -> "10:00"
   *   "13:45"      -> "13:45"     (already formatted)
   *   "1345"       -> "13:45"     (raw 4 digits)
   *   ":15"        -> "00:15"     (minutes only via colon)
   *   ".5" / ".25" -> "00:30" / "00:15"  (decimal hours)
   *   "1.5"        -> "01:30"     (decimal hours > 1)
   * Anything it can't make sense of -> "00:00".
   */
  const parseUserTime = (raw) => {
    if (raw === null || raw === undefined) return EMPTY_TIME;
    const value = String(raw).trim();
    if (!value || value === "__:__") return EMPTY_TIME;

    // Case 1: starts with a colon -> minutes only, e.g. ":15"
    if (value.startsWith(":")) {
      const minutes = Number(value.slice(1));
      if (Number.isNaN(minutes) || minutes < 0 || minutes > 59) return EMPTY_TIME;
      return `00:${String(minutes).padStart(2, "0")}`;
    }

    // Case 2: decimal hours, e.g. ".5", ".25", "1.75", "2.5"
    if (value.includes(".")) {
      const asFloat = Number(value);
      if (Number.isNaN(asFloat) || asFloat < 0) return EMPTY_TIME;
      const totalMinutes = Math.round(asFloat * 60);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      if (hours > 23) return EMPTY_TIME;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }

    // Case 3: explicit HH:MM with a colon somewhere in the middle
    if (value.includes(":")) {
      const parts = value.split(":");
      if (parts.length !== 2) return EMPTY_TIME;
      const hours = Number(parts[0] || "0");
      const minutes = Number(parts[1] || "0");
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return EMPTY_TIME;
      if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return EMPTY_TIME;
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }

    // Case 4: pure digits, no colon, no dot
    if (/^\d+$/.test(value)) {
      if (value.length <= 2) {
        // "1" -> 01:00, "10" -> 10:00
        const hours = Number(value);
        if (hours > 23) return EMPTY_TIME;
        return `${String(hours).padStart(2, "0")}:00`;
      }
      if (value.length === 3) {
        // "130" -> 1:30
        const hours = Number(value.slice(0, 1));
        const minutes = Number(value.slice(1));
        if (hours > 23 || minutes > 59) return EMPTY_TIME;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      }
      if (value.length === 4) {
        // "1345" -> 13:45
        const hours = Number(value.slice(0, 2));
        const minutes = Number(value.slice(2));
        if (hours > 23 || minutes > 59) return EMPTY_TIME;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
      }
      return EMPTY_TIME;
    }

    return EMPTY_TIME;
  };

  const isNormalizedTimeValue = (value) => {
    if (typeof value !== "string") return false;
    if (!/^\d{2}:\d{2}$/.test(value)) return false;
    return normalizeTimeValue(value) === value;
  };

  useEffect(() => {
    let technician = _.cloneDeep(tech);
    if (tech?.timesheet) {
      for (let i = 0; i < tech.timesheet.length; i++) {
        const workDate = moment(startDateString).add(Number(tech.timesheet[i].dayDiff || 0), "days");
        const isSameDate = workDate.isSame(currentDate, "day");

        if (isSameDate) {
          technician.regularTime = tech.timesheet[i].regularTime;
          technician.travelTime = tech.timesheet[i].travelTime;
          technician.overTime = tech.timesheet[i].overTime;
        }
      }
    }

    setTechData(technician);
    const normalized = {
      regularTime: normalizeTimeValue(technician.regularTime),
      travelTime: normalizeTimeValue(technician.travelTime),
      overTime: normalizeTimeValue(technician.overTime),
    };
    setEditedTimes(normalized);
    baselineTimesRef.current = normalized;
  }, [tech, currentDate, startDateString]);

  /**
   * When the user focuses a field:
   *  - If it's "00:00", wipe it so the cursor blinks in an empty box.
   *  - Otherwise select all so typing replaces the value (and triple-click
   *    still selects all, as expected).
   */
  const handleFieldFocus = (fieldName, event) => {
    const currentValue = editedTimes[fieldName];
    if (currentValue === EMPTY_TIME || currentValue === "") {
      setEditedTimes((previous) => ({ ...previous, [fieldName]: "" }));
      return;
    }
    // Defer so the select happens after focus settles.
    const input = event.target;
    setTimeout(() => {
      try {
        input.select();
      } catch (_err) {
        /* no-op */
      }
    }, 0);
  };

  /**
   * Commit a single field: parse the raw text, normalize, and store it back.
   * Empty input collapses back to "00:00".
   */
  const commitField = (fieldName) => {
    setEditedTimes((previous) => {
      const raw = previous[fieldName];
      const normalized = raw === "" ? EMPTY_TIME : parseUserTime(raw);
      if (normalized === previous[fieldName]) return previous;
      return { ...previous, [fieldName]: normalized };
    });
  };

  const handleFieldKeyDown = (fieldName, event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      // Parse all three fields synchronously so we don't race React state.
      const finalTimes = {
        regularTime: editedTimes.regularTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.regularTime),
        travelTime: editedTimes.travelTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.travelTime),
        overTime: editedTimes.overTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.overTime),
      };
      setEditedTimes(finalTimes);
      updateTimesheet(finalTimes);
      setActiveEditingTechId(null);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      // Revert this field to its baseline and exit edit mode.
      setEditedTimes((previous) => ({
        ...previous,
        [fieldName]: baselineTimesRef.current[fieldName],
      }));
      setActiveEditingTechId(null);
    }
  };

  const renderTimeField = (fieldName) => {
    if (!isEditing) {
      const value = techData?.[fieldName];
      return (
        <p className="font-OpenSansRegular text-[14px] font-[400] text-titleColor w-[96px] text-center">
          {value && value !== EMPTY_TIME ? value : "__:__"}
        </p>
      );
    }

    return (
      <input
        value={editedTimes[fieldName]}
        onFocus={(e) => handleFieldFocus(fieldName, e)}
        onBlur={() => commitField(fieldName)}
        onKeyDown={(e) => handleFieldKeyDown(fieldName, e)}
        onChange={(e) => {
          let nextValue = e.target.value.slice(0, 6);
          const previousValue = editedTimes[fieldName] || "";
          const isDeleting = nextValue.length < previousValue.length;

          // If the user typed a "." right after an auto-inserted colon
          // (e.g. "12:" + "." -> "12:."), strip the colon so "12.5" parses
          // as decimal hours.
          if (nextValue.includes(":.")) {
            nextValue = nextValue.replace(":.", ".");
          }

          // When deleting, also strip a trailing colon so backspace can get
          // past the auto-inserted ":" (e.g. "12:25" -> "12:2" -> "12:" ->
          // this strips to "12", next backspace -> "1").
          if (isDeleting && nextValue.endsWith(":")) {
            nextValue = nextValue.slice(0, -1);
          }

          // Auto-insert a colon after two digits, but only when the user is
          // typing forward (not deleting) and the value is pure digits.
          // Leaves ".5", ":15", "1.5", and mid-delete states alone.
          if (!isDeleting && /^\d+$/.test(nextValue) && nextValue.length >= 2) {
            nextValue = nextValue.length === 2 ? `${nextValue}:` : `${nextValue.slice(0, 2)}:${nextValue.slice(2, 4)}`;
          }
          setEditedTimes((previous) => ({ ...previous, [fieldName]: nextValue }));
        }}
        className="w-[97px] h-[34px] text-center rounded-[10px] border-[1px] border-borderColor bg-white font-OpenSansRegular text-[14px] text-titleColor outline-none"
      />
    );
  };

  const onEditButtonClick = () => {
    if (!isEditing) {
      if (activeEditingTechId && activeEditingTechId !== tech.id) {
        return;
      }
      // Snapshot the baseline at the moment we enter edit mode.
      baselineTimesRef.current = {
        regularTime: normalizeTimeValue(techData?.regularTime),
        travelTime: normalizeTimeValue(techData?.travelTime),
        overTime: normalizeTimeValue(techData?.overTime),
      };
      setEditedTimes(baselineTimesRef.current);
      setActiveEditingTechId(tech.id);
      return;
    }

    // Normalize all three fields before saving in case the user clicked the
    // check mark without blurring the input first.
    const finalTimes = {
      regularTime: editedTimes.regularTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.regularTime),
      travelTime: editedTimes.travelTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.travelTime),
      overTime: editedTimes.overTime === "" ? EMPTY_TIME : parseUserTime(editedTimes.overTime),
    };
    setEditedTimes(finalTimes);
    updateTimesheet(finalTimes);
    setActiveEditingTechId(null);
  };

  const updateTimesheet = (overrideTimes) => {
    if (isSubmitting) {
      return;
    }
    const finalTimes = overrideTimes || editedTimes;
    const originalTimes = baselineTimesRef.current;

    let changedTimes = {};
    if (finalTimes.regularTime !== originalTimes.regularTime && isNormalizedTimeValue(finalTimes.regularTime)) {
      changedTimes.regularTime = finalTimes.regularTime;
    }
    if (finalTimes.travelTime !== originalTimes.travelTime && isNormalizedTimeValue(finalTimes.travelTime)) {
      changedTimes.travelTime = finalTimes.travelTime;
    }
    if (finalTimes.overTime !== originalTimes.overTime && isNormalizedTimeValue(finalTimes.overTime)) {
      changedTimes.overTime = finalTimes.overTime;
    }

    if (Object.keys(changedTimes).length === 0) {
      return;
    }

    const dayDiff = moment(currentDate).diff(moment(startDateString), "days");

    let timesheet = [];
    if (techData.timesheet && techData.timesheet.length > 0) {
      timesheet = _.cloneDeep(techData.timesheet);
      if (timesheet) {
        let isFound = false;
        for (let i = 0; i < timesheet.length; i++) {
          if (timesheet[i].dayDiff === dayDiff) {
            if (changedTimes.regularTime) {
              timesheet[i].regularTime = changedTimes.regularTime;
            }
            if (changedTimes.travelTime) {
              timesheet[i].travelTime = changedTimes.travelTime;
            }
            if (changedTimes.overTime) {
              timesheet[i].overTime = changedTimes.overTime;
            }
            isFound = true;
          }
        }
        if (!isFound) {
          let temp = _.cloneDeep(changedTimes);
          temp.dayDiff = dayDiff;
          timesheet.push(temp);
        }
      }
    } else {
      let temp = _.cloneDeep(changedTimes);
      temp.dayDiff = dayDiff;
      timesheet.push(temp);
    }

    setIsSubmitting(true);
    TechnicianService.updateTimesheet({
      id: techData.id,
      data: { timesheet: JSON.stringify(timesheet) },
    })
      .then((response) => {
        toast("Timesheet updated successfully");
        timesheetUpdated();
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    techData && (
      <div className="w-full h-[40px] flex items-center px-3 border-t-[1px] border-borderColor relative">
        <img src={getAvatarUrl(techData.user.avatar)} className="w-[24px] rounded-[12px]" />
        <p className="font-OpenSansRegular text-[14px] font-[400] text-titleColor ml-2 flex-1 min-w-0 truncate mr-[calc(5%+325px)]">
          {techData.user.name}
        </p>

        <div className="absolute right-[5%] top-[3px] h-[34px] flex items-center gap-3">
          {renderTimeField("regularTime")}
          {renderTimeField("travelTime")}
          {renderTimeField("overTime")}
        </div>

        {/* Timesheet save always hits /update_timesheet_staff (wo:logTimesheet-gated),
          so logging on a billed WO needs wo:logTimesheet AND the extra
          wo:logBilledTimesheet — the latter unlocks editing once billed. */}
        {!isFutureStartDate &&
          ((woStatus < 100 && can("wo:logTimesheet")) ||
            (woStatus === 100 && can("wo:logBilledTimesheet") && (
              <div className="absolute right-[1%] top-[3px]">
                <EditButton
                  clicked={onEditButtonClick}
                  isEditing={isEditing}
                  isDisabled={!isEditing && activeEditingTechId !== null && activeEditingTechId !== tech.id}
                />
              </div>
            )))}
      </div>
    )
  );
};

export default DayTechItem;

const EditButton = ({ clicked, isEditing = false, isDisabled = false }) => {
  // When editing, swallow mousedown so the focused input doesn't blur before
  // our click handler runs. Blur would fire commitField and race with the
  // save path, leaving us with stale state.
  const handleMouseDown = (event) => {
    if (isEditing) {
      event.preventDefault();
    }
  };

  return isEditing ? (
    <div
      onMouseDown={handleMouseDown}
      onClick={clicked}
      className="w-[18px] absolute right-[5%] top-[12px] cursor-pointer"
    >
      <img src={getImageUrl("/images/common/check.png")} className="w-[18px]" />
    </div>
  ) : (
    <div
      onClick={isDisabled ? undefined : clicked}
      className={`w-[18px] absolute right-[5%] top-[12px] ${isDisabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
    >
      <img src={getImageUrl("/images/common/blueedit.png")} className="w-[18px]" />
    </div>
  );
};
